import React, { useCallback, useRef, useState } from 'react';
import { AnimatePresence, MotionConfig, motion, useReducedMotion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react';
import { projects } from '../../../data/projects';
import { clients } from '../../../data/clients';
import { useProjectDeck } from '../../../hooks/useProjectDeck';
import { CARD_RADIUS, ProjectCard } from './ProjectCard';
import { ProjectDirectory } from './ProjectDirectory';
import { ClientDirectory } from './ClientDirectory';
const STACK_DEPTH = 2;
export function ProjectDeck() {
  const {
    index,
    direction,
    isFlipped,
    next,
    previous,
    goTo,
    toggleFlip
  } = useProjectDeck(projects.length);
  const reduceMotion = useReducedMotion() ?? false;
  const current = projects[index];
  const topRef = useRef<HTMLDivElement>(null);
  const [isDirectoryOpen, setIsDirectoryOpen] = useState(false);
  const closeDirectory = useCallback(() => setIsDirectoryOpen(false), []);
  const openProject = (projectId: string) => {
    const target = projects.findIndex(project => project.id === projectId);
    if (target === -1) return;
    setIsDirectoryOpen(false);
    goTo(target);
    topRef.current?.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'start'
    });
  };
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      next();
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      previous();
    }
  };
  return <section id="work" className="w-full py-20 md:py-32" style={{
    background: 'var(--section-bg)'
  }} aria-label="Selected work" data-id="element-197" data-mp="2vlfg4vyswooh">
      <div className="mx-auto w-full max-w-[1520px] px-5 sm:px-6 md:px-10 lg:px-14" data-id="element-198" data-mp="35ca1ly483k8v">
        <div ref={topRef} className="mb-8 flex scroll-mt-24 flex-col gap-5 sm:flex-row sm:items-end sm:justify-between sm:gap-6 md:mb-12" data-id="element-199" data-mp="35env42j98oeb">
          <h2 className="text-4xl tracking-tight md:text-6xl" style={{
          color: 'var(--fg)'
        }} data-id="element-200" data-mp="3edrel7mc35tc">
            Selected work
          </h2>
          <div className="flex items-center gap-3" data-id="element-201" data-mp="2c150a2zd7bav">
            <button type="button" onClick={previous} className="flex h-12 w-12 items-center justify-center rounded-full transition-colors duration-150 ease-out" style={{
            border: '1px solid var(--border)',
            color: 'var(--fg)',
            background: 'var(--card)'
          }} aria-label="Previous project" data-id="element-202" data-mp="2g0nj8t7mlcb2">
              <ArrowLeftIcon size={18} data-id="element-203" data-mp="2use90a1b9w1e" />
            </button>
            <p className="font-aspekta w-14 text-center text-sm tabular-nums" style={{
            color: 'var(--muted)'
          }} aria-live="polite" data-id="element-204" data-mp="2zntkivbujfun">
              <span style={{
              color: 'var(--fg)'
            }} data-id="element-205" data-mp="33on6vk9n4mlf">{index + 1}</span> / {projects.length}
              <span className="sr-only" data-id="element-206" data-mp="38p1gv7usy6m3">: {current.name}</span>
            </p>
            <button type="button" onClick={next} className="flex h-12 w-12 items-center justify-center rounded-full" style={{
            background: 'var(--accent)',
            color: '#FFFFFF',
            border: 'none'
          }} aria-label="Next project" data-id="element-207" data-mp="19e0ccd6beivf">
              <ArrowRightIcon size={18} data-id="element-208" data-mp="120sjd4ns5chk" />
            </button>
          </div>
        </div>

        <ProjectDirectory projects={projects} activeIndex={index} reduceMotion={reduceMotion} onSelect={goTo} data-id="element-209" data-mp="x393y1fcito1" />

        <div className="relative mt-8 h-[580px] w-full outline-none focus-visible:ring-2 focus-visible:ring-offset-4 sm:h-[640px] md:mt-12 md:h-[720px] lg:h-[660px]" tabIndex={0} onKeyDown={handleKeyDown} role="group" aria-roledescription="carousel" aria-label="Project cards. Drag, or use the left and right arrow keys, to browse." data-id="element-210" data-mp="1pqwl16her25x">
          {Array.from({
          length: STACK_DEPTH
        }, (_, i) => STACK_DEPTH - i).map(depth => <motion.div key={`stack-${depth}`} className="pointer-events-none absolute inset-0" style={{
          zIndex: 3 - depth,
          borderRadius: CARD_RADIUS,
          border: '1px solid var(--border)',
          background: 'var(--card)'
        }} initial={false} animate={{
          y: depth * 12,
          scale: 1 - depth * 0.025
        }} aria-hidden="true" data-id="element-211" data-mp="cgl4w112nik" />)}

          <MotionConfig reducedMotion="user" data-id="element-212" data-mp="26ze8b8ndxvgo">
            <AnimatePresence initial={false} custom={direction} data-id="element-213" data-mp="270pch9kqt58b">
              <ProjectCard key={current.id} project={current} isFlipped={isFlipped} reduceMotion={reduceMotion} onFlip={toggleFlip} onSwipe={dir => dir === 1 ? next() : previous()} data-id="element-214" data-mp="2bz0nhnmrxls4" />
            </AnimatePresence>
          </MotionConfig>
        </div>

        <div className="mt-12 flex flex-col items-center gap-5" data-id="element-215" data-mp="mlng1pjd7hms">
          <p className="text-center text-sm" style={{
          color: 'var(--muted)'
        }} data-id="element-216" data-mp="qlxl8zzc240l">
            Drag the card or use the arrows to browse. Flip a card to read the full story.
          </p>
          <button type="button" onClick={() => setIsDirectoryOpen(true)} className="font-aspekta inline-flex h-12 items-center gap-2 whitespace-nowrap rounded-full px-7 text-base transition-colors duration-150 ease-out" style={{
          border: '1px solid var(--fg)',
          color: 'var(--fg)',
          background: 'transparent'
        }} onMouseEnter={e => {
          e.currentTarget.style.background = 'var(--fg)';
          e.currentTarget.style.color = 'var(--bg)';
        }} onMouseLeave={e => {
          e.currentTarget.style.background = 'transparent';
          e.currentTarget.style.color = 'var(--fg)';
        }} data-id="element-217" data-mp="e3brcsnfhac4">
            See all clients
            <span className="font-aspekta text-sm opacity-60" data-id="element-218" data-mp="3do7b1pbhqowk">{clients.length}</span>
          </button>
        </div>

        <ClientDirectory isOpen={isDirectoryOpen} reduceMotion={reduceMotion} onClose={closeDirectory} onOpenProject={openProject} data-id="element-219" data-mp="38p4ekt9ktr07" />
      </div>
    </section>;
}